import React, { useState, useEffect } from 'react';
import { FaPollH, FaTimes } from 'react-icons/fa';
import styled, { keyframes, css } from 'styled-components';
import SimplePoll from '../sections/Sondage';
import ClubDuMoisLogo from '../../assets/Club du mois.svg';
import { useDeviceDetection } from '../../hooks/useDeviceDetection';

// Animation de rebond du bouton
const bounce = keyframes`
  0%, 100% { 
    right: 20px; 
  }
  
  /* Déplacement vers la gauche */
  5% { 
    right: 60px; 
  }
  
  /* Premier rebond */
  10% { 
    right: 0px; 
    transform: rotate(5deg);
  }
  
  /* Retrait après le premier rebond */
  15% { 
    right: 40px; 
  }
  
  /* Deuxième rebond */
  20% { 
    right: 5px; 
    transform: rotate(-5deg);
  }
  
  /* Retour à la position initiale */
  25% { 
    right: 20px; 
  }
`;

// Conteneur du bouton flottant (desktop/tablette uniquement)
const ButtonWrapper = styled.div`
  position: fixed;
  right: 0px;
  top: 120px; /* Position fixe en dessous du header (environ 120px de haut) */
  z-index: ${props => props.$menuBlur ? 10 : 1000};
  display: flex;
  flex-direction: column;
  align-items: center;
  
  ${props => !props.$hasClicked && css`
    animation: ${bounce} 10s ease-in-out 2s infinite;
    animation-fill-mode: both;
  `}

  /* Masqué sur mobile */
  @media (max-width: 768px) {
    display: none;
  }
`;

// Bouton flottant avec logo
const FloatingButton = styled.button`
  background: transparent;
  color: white;
  border: none;
  border-radius: 50%;
  width: 160px;
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: none;
  transition: transform var(--transition-normal);
  padding: 0;
  
  &:hover {
    transform: scale(1.1);
    background: transparent;
    box-shadow: none;
  }
  
  svg {
    font-size: 1.5rem;
  }
`;

// Texte courbé au-dessus du bouton
const CurvedText = styled.div`
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  width: 120px;
  height: 30px;
  font-size: 16px;
  font-weight: bold;
  color: #3643BA;
  text-align: center;
  pointer-events: none;
  transition: transform var(--transition-normal);
  
  svg {
    width: 100%;
    height: 100%;
    overflow: visible;
    
    text {
      fill: #3643BA;
      stroke: #fff;
      stroke-width: 1px;
      paint-order: stroke fill;
      font-family: Arial, sans-serif;
      font-size: 16px;
      font-weight: bold;
      letter-spacing: 1px;
      transition: transform var(--transition-normal);
    }
  }
`;



// Overlay de la popup
const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: flex-end;
  align-items: center;
  z-index: 1001;
`;

// Conteneur de la popup
const PopupContainer = styled.div`
  background: white;
  width: 500px;
  max-width: 90vw;
  height: 80vh;
  margin-right: 20px;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-xl);
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    width: 95vw;
    height: 90vh;
    margin: 0;
  }
`;

// Header de la popup
const PopupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--gray-lighter);
  background: var(--light);
`;

// Titre de la popup
const PopupTitle = styled.h3`
  margin: 0;
  color: var(--dark);
  font-size: var(--font-size-lg);
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 0.5rem;
    color: var(--primary);
  }
`;

// Bouton de fermeture
const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--gray);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all var(--transition-normal);
  
  &:hover {
    background: var(--gray-lighter);
    color: var(--dark);
  }
`;

// Contenu de la popup
const PopupContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 10px;
`;

// Composant principal
const FloatingSondageButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasClicked, setHasClicked] = useState(false);
  const [menuBlur, setMenuBlur] = useState(false);
  const deviceInfo = useDeviceDetection();

  // Ouvrir/fermer la popup
  const togglePopup = () => {
    setIsOpen(!isOpen);
    
    if (!hasClicked) {
      setHasClicked(true);
    }
  };

  // Vérifier si le menu principal est flouté
  useEffect(() => {
    const checkMenuBlur = () => {
      setMenuBlur(document.body.classList.contains('menu-blur'));
    };
    
    checkMenuBlur();
    window.addEventListener('resize', checkMenuBlur);
    
    const observer = new MutationObserver(checkMenuBlur);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    
    return () => {
      window.removeEventListener('resize', checkMenuBlur);
      observer.disconnect();
    };
  }, []);

  // Écouter l'événement d'ouverture du sondage depuis la navbar mobile
  useEffect(() => {
    const handleOpenSondage = () => {
      setIsOpen(true);
      if (!hasClicked) {
        setHasClicked(true);
      }
    };

    window.addEventListener('openSondage', handleOpenSondage);
    
    return () => {
      window.removeEventListener('openSondage', handleOpenSondage);
    };
  }, [hasClicked]);

  return (
    <>
      {/* Bouton flottant pour desktop/tablette */}
      {deviceInfo.renderOnDesktop(
        <ButtonWrapper $hasClicked={hasClicked} $menuBlur={menuBlur}>
          <FloatingButton onClick={togglePopup}>
            <img 
              src={ClubDuMoisLogo} 
              alt="Club du mois" 
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </FloatingButton>
        </ButtonWrapper>
      )}



      {/* Popup commune pour tous les appareils */}
      {isOpen && (
        <PopupOverlay onClick={togglePopup}>
          <PopupContainer onClick={(e) => e.stopPropagation()}>
            <PopupHeader>
              <PopupTitle>
                <FaPollH />
                Club du mois décathlon
              </PopupTitle>
              <CloseButton onClick={togglePopup}>
                <FaTimes />
              </CloseButton>
            </PopupHeader>
            
            <PopupContent>
              <SimplePoll />
            </PopupContent>
          </PopupContainer>
        </PopupOverlay>
      )}
    </>
  );
};

export default FloatingSondageButton; 